import { Component, DestroyRef, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SELECT_INVALID_SOLDO_FIELD, collectFormErrors, scrollToFirstResult } from '@shared/formHelpers';
import { Contact, ContactRole, CountryMetadata, addContactRole } from '@shared/models/data-model';
import { RegistrationHelper } from '@shared/registration-helper';
import { AmplitudeService } from '@shared/services/amplitude.service';
import { CountryService } from '@shared/services/country.service';
import { DTRUM_ACTION_NAME, DynatraceHelperService } from '@shared/services/dynatraceHelper.service';
import { LoadingScreenService } from '@shared/services/loading-screen.service';
import { RegistrationService } from '@shared/services/registration.service';
import { REGISTRATION_STEP_ID } from '@shared/services/steps.service';
import { APP_CONFIG, AppConfig } from '@src/app/app-config.module';
import { NGXLogger } from 'ngx-logger';
import { PersonFormGroup, PersonFormHelperService } from '../person-form/person-form-helper.service';

export enum ADMIN_CHOICE {
    NO_CHOICE,
    APPLICANT,
    LEGAL_REPRESENTATIVE,
    OTHER
}

interface AdminChoiceFormGroup {
    adminChoice: FormControl<ADMIN_CHOICE>;
    admin: FormGroup<PersonFormGroup>;
}

@Component({
    selector: 'admin',
    templateUrl: './admin.component.html',
    styleUrl: './admin.component.scss'
})
export class AdminComponent implements OnInit {

    private readonly stepId = REGISTRATION_STEP_ID.ADMIN;
    contactsDTO: Contact[] = [];
    form: FormGroup<AdminChoiceFormGroup>;
    loading: boolean = true;
    applicantIsLegalRep: boolean;
    countryOfIncorporation: string;
    readonly: boolean = false;
    regulatoryInstitution: string;
    registrationProcessURL: string;
    translatedCountries: CountryMetadata[] = [];

    constructor(
        @Inject(APP_CONFIG) private readonly appConfig: AppConfig,
        protected logger: NGXLogger,
        private readonly router: Router,
        private readonly fb: FormBuilder,
        private readonly personFormHelper: PersonFormHelperService,
        private readonly registrationService: RegistrationService,
        private readonly amplitudeService: AmplitudeService,
        private readonly route: ActivatedRoute,
        private readonly dynatrace: DynatraceHelperService,
        private readonly countryService: CountryService,
        private readonly translate: TranslateService,
        private readonly loadingScreenService: LoadingScreenService,
        private readonly destroyRef: DestroyRef
    ) { }

    public get applicantContact(): Contact {
        return this.contactsDTO.find(c => c.roles.includes(ContactRole.Applicant));
    }

    public get legalRepContact(): Contact {
        return this.contactsDTO.find(c => c.roles.includes(ContactRole.LegalRepresentative));
    }

    ngOnInit(): void {
        this.contactsDTO = this.route.snapshot.data.contacts;
        const company = this.route.snapshot.data.company;
        this.countryOfIncorporation = company.registeredAddress?.countryISO;
        this.regulatoryInstitution = this.appConfig.regulatoryInstitutionShorthand[company.eMoneyLicenseIssuer];

        this.registrationProcessURL = this.appConfig.registrationProcessHelpURLs[this.countryOfIncorporation]
            || this.appConfig.registrationProcessHelpURLs.fallback;

        this.applicantIsLegalRep = this.applicantContact.roles.includes(ContactRole.LegalRepresentative);

        this.translatedCountries = this.route.snapshot.data?.countryList;
        this.translate.onLangChange.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(async () => {
            this.loadingScreenService.show('LOADING_SCREEN.COUNTRY_TRANSLATION');
            this.translatedCountries = await this.countryService.getCurrentCountries();
            this.loadingScreenService.hide();
        });

        const adminContact = this.contactsDTO.find(c => c.roles.includes(ContactRole.Admin));

        if (adminContact) {
            this.initAsExistingAdmin(adminContact);
        } else {
            this.initAsNew();
        }
        this.form.controls.adminChoice.valueChanges.subscribe((choice) => {
            if (choice == ADMIN_CHOICE.APPLICANT) {
                this.selectApplicant();
            } else if (choice === ADMIN_CHOICE.LEGAL_REPRESENTATIVE) {
                this.selectLegalRepresentative();
            } else if (choice === ADMIN_CHOICE.OTHER) {
                this.selectNewContact();
            }
        });
        this.loading = false;

    }

    private initAsNew() {
        this.form = this.fb.group({
            adminChoice: [ADMIN_CHOICE.NO_CHOICE, (control: AbstractControl) => {
                return (control.value === ADMIN_CHOICE.NO_CHOICE) ? { required: true } : null;
            }],
            admin: this.personFormHelper.createEmptyPersonFormGroup(this.countryOfIncorporation)
        });
    }

    private initAsExistingAdmin(adminContact: Contact) {
        let previousChoice: ADMIN_CHOICE = ADMIN_CHOICE.OTHER;
        if (adminContact.roles.includes(ContactRole.LegalRepresentative)) {
            previousChoice = ADMIN_CHOICE.LEGAL_REPRESENTATIVE;
            this.readonly = true;
        }
        if (adminContact.roles.includes(ContactRole.Applicant)) {
            previousChoice = ADMIN_CHOICE.APPLICANT;
        }

        this.form = this.fb.group({
            adminChoice: [previousChoice as ADMIN_CHOICE],
            admin: this.personFormHelper.createPersonFormGroupForContact(adminContact, this.countryOfIncorporation)
        });
    }

    selectApplicant(): void {
        this.readonly = this.applicantContact.roles.includes(ContactRole.LegalRepresentative);

        const adminFormGroup = this.personFormHelper.createPersonFormGroupForContact(this.applicantContact, this.countryOfIncorporation);
        this.form.setControl("admin", adminFormGroup);
    }

    selectLegalRepresentative(): void {
        this.readonly = true;

        const adminFormGroup = this.personFormHelper.createPersonFormGroupForContact(this.legalRepContact, this.countryOfIncorporation);
        this.form.setControl("admin", adminFormGroup);
    }

    selectNewContact(): void {
        this.readonly = false;

        const adminFormGroup = this.personFormHelper.createEmptyPersonFormGroup(this.countryOfIncorporation);
        adminFormGroup.controls.roles.setValue([]);
        this.form.setControl("admin", adminFormGroup);
    }

    private async buildContactsToSave(): Promise<Contact[]> {
        this.logger.debug("Admin Values: ", this.form.value.admin);
        const adminContact = await this.personFormHelper.mapFormValuesToContact(this.form.controls.admin);
        const originalContact = this.contactsDTO.find(c => c.id === adminContact.id);
        if (originalContact) {
            adminContact.roles = [...originalContact.roles];
        }
        addContactRole(adminContact, ContactRole.Admin);

        const contactsToSave: Contact[] = [];
        if (originalContact) {
            contactsToSave.push(RegistrationHelper.mergeContacts(originalContact, adminContact, adminContact.roles));
        } else {
            contactsToSave.push(RegistrationHelper.cloneContact(adminContact, adminContact.roles));
        }

        for (const c of this.contactsDTO) {
            if (c.id !== adminContact.id) {
                const roles = c.roles.filter(r => r !== ContactRole.Admin);
                if (roles.length > 0) {
                    contactsToSave.push(RegistrationHelper.cloneContact(c, roles));
                }
            }
        }
        return contactsToSave;
    }

    async onSubmit() {
        if (this.form.value.adminChoice === ADMIN_CHOICE.NO_CHOICE) {
            this.form.controls.adminChoice.markAsTouched();
            this.amplitudeService.trackFormSubmissionRejected(this.route.snapshot.data['pageName'], collectFormErrors(this.form, { adminChoice: 'required' }));
            scrollToFirstResult(SELECT_INVALID_SOLDO_FIELD);
            return;
        }

        this.form.markAllAsTouched();
        if (this.form.valid) {
            this.loading = true;

            const submitActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.SUBMIT_FORM);

            const contactsToSave = await this.buildContactsToSave();
            this.registrationService.saveAllContacts(contactsToSave).then(() => {
                const admin = contactsToSave.find(c => c.roles.includes(ContactRole.Admin));
                this.amplitudeService.trackAccountAdminChoice(admin.roles.includes(ContactRole.Applicant), admin.roles.includes(ContactRole.LegalRepresentative));
                this.registrationService.completeStep(this.stepId);
                this.router.navigate([this.registrationService.getNextStep()]).then(() => {
                    this.dynatrace.leaveAction(submitActionId);
                });
            }, (err) => {
                this.logger.trace("FATAL: ", err);
                this.dynatrace.leaveAction(submitActionId);
            });
        } else {
            this.loading = false;
            this.amplitudeService.trackFormSubmissionRejected(this.route.snapshot.data['pageName'], collectFormErrors(this.form));
            this.logger.warn("form not valid: ", this.form.value, this.form.errors);
            scrollToFirstResult(SELECT_INVALID_SOLDO_FIELD, 'fiscal-code:has(input.ng-invalid)');
        }
    }

    goToPrevious() {
        const backActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.BACK_TO_PREVIOUS);
        this.router.navigate([this.registrationService.getPreviousStep()]).then(() => {
            this.dynatrace.leaveAction(backActionId);
        });
    }

    public get ADMIN_CHOICE() {
        return ADMIN_CHOICE;
    }
}
